import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { resolveImg, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ImageUploader } from "@/components/ImageUploader";

const inputCls = "w-full border border-input bg-white px-4 py-3 text-sm text-stone-900 outline-none placeholder:text-stone-400 focus:border-wine";
const empty = { name: "", category: "", subcategory: "", price: "", cttw: "", description: "", images: [], variants: [], featured: false, sort_order: 0 };

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty, category: categories[0]?.slug || "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, price: p.price ?? "", variants: p.variants || [] }); setOpen(true); };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { cttw: "", price: "" }] }));
  const updVariant = (i, key, val) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)) }));
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));


  const save = async () => {
    if (!form.name || !form.category) { toast.error("Name and category are required."); return; }
    if (!form.images.length) { toast.error("Please add at least one image."); return; }
    setSaving(true);
    const variants = (form.variants || [])
      .filter((v) => v.cttw && v.cttw.trim())
      .map((v) => ({ cttw: v.cttw.trim(), price: v.price === "" || v.price == null ? null : Number(v.price) }));
    const payload = { ...form, price: form.price === "" ? null : Number(form.price), sort_order: Number(form.sort_order) || 0, variants };
    try {
      if (editing) { await api.put(`/products/${editing.id}`, payload); toast.success("Piece updated."); }
      else { await api.post("/products", payload); toast.success("Piece added."); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/products/${toDelete.id}`); toast.success("Piece removed."); setToDelete(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  return (
    <div data-testid="admin-products">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">Products</h2>
          <p className="mt-1 text-xs text-stone-400">{products.length} pieces</p>
        </div>
        <button data-testid="add-product-btn" onClick={openNew} className="flex items-center gap-2 bg-wine px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-wine-hover">
          <Plus size={15} /> Add piece
        </button>
      </div>

      <div className="border border-border">
        <table className="w-full text-left" data-testid="products-table">
          <thead className="border-b border-border bg-secondary text-[10px] uppercase tracking-[0.2em] text-stone-500">
            <tr>
              <th className="px-5 py-4 font-normal">Piece</th>
              <th className="hidden px-5 py-4 font-normal md:table-cell">Category</th>
              <th className="px-5 py-4 font-normal">Price</th>
              <th className="hidden px-5 py-4 font-normal sm:table-cell">Featured</th>
              <th className="px-5 py-4 text-right font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`row-${p.id}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-4">
                    <img src={resolveImg(p.images?.[0])} alt="" className="h-12 w-12 object-cover" />
                    <div>
                      <div className="font-serif text-base text-stone-900">{p.name}</div>
                    </div>
                  </div>
                </td>
                <td className="hidden px-5 py-4 text-sm text-stone-500 md:table-cell">{catName(p.category)}</td>
                <td className="px-5 py-4 text-sm text-wine">{formatPrice(p.price)}</td>
                <td className="hidden px-5 py-4 sm:table-cell">{p.featured ? <span className="text-xs text-wine">Yes</span> : <span className="text-xs text-stone-300">—</span>}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="border border-border p-2 text-stone-500 hover:border-wine hover:text-wine"><Pencil size={14} /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => setToDelete(p)} className="border border-border p-2 text-stone-500 hover:border-red-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{editing ? "Edit piece" : "New piece"}</DialogTitle><DialogDescription className="sr-only">Product details form</DialogDescription></DialogHeader>
          <div className="space-y-5 pt-2">
            <Field label="Images"><ImageUploader images={form.images} onChange={(images) => setForm({ ...form, images })} onUploadingChange={setImgUploading} /></Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Name"><input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
              <Field label="Price (USD)"><input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} placeholder="Optional" /></Field>
              <Field label="CTTW (carat total weight)"><input data-testid="form-cttw" value={form.cttw} onChange={(e) => setForm({ ...form, cttw: e.target.value })} className={inputCls} placeholder="e.g. 1.50 ctw" /></Field>
              <Field label="Category">
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  <option value="">Select…</option>
                  {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Short description"><input data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="One line (shown only on the item page)" /></Field>
            <Field label="Price table (optional — shows CTTW & price instead of a single price)">
              <div className="space-y-2" data-testid="variants-editor">
                {(form.variants || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={v.cttw} onChange={(e) => updVariant(i, "cttw", e.target.value)} placeholder="CTTW e.g. 1.00 ct" className={inputCls} data-testid={`variant-cttw-${i}`} />
                    <input type="number" value={v.price ?? ""} onChange={(e) => updVariant(i, "price", e.target.value)} placeholder="Price" className={inputCls} data-testid={`variant-price-${i}`} />
                    <button type="button" onClick={() => rmVariant(i)} data-testid={`remove-variant-${i}`} className="shrink-0 border border-border p-2.5 text-stone-500 hover:border-red-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" onClick={addVariant} data-testid="add-variant-btn" className="flex items-center gap-2 border border-border px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-stone-600 hover:border-wine hover:text-wine"><Plus size={14} /> Add row</button>
              </div>
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-600">
                <input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#8C1D2A]" />
                Featured on homepage
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setOpen(false)} className="border border-border px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">Cancel</button>
            <button data-testid="save-product-btn" onClick={save} disabled={saving || imgUploading} className="bg-wine px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-wine-hover disabled:opacity-60">{imgUploading ? "Uploading…" : saving ? "Saving…" : "Save"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif font-normal">Remove this piece?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product" onClick={remove} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</label>
    {children}
  </div>
);
